import * as React from 'react';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import '../../commonCPStyles.scss';
import {Dispatch} from 'redux';
import {ContentComponent} from 'Components/ContentComponent/ContentComponent';
import {Pagination, Select, Space, Table} from 'antd';
import {IJobScheduleData} from 'helpers/interfaces';
import {HeaderTitleComponent} from 'Components/ContentComponent/HeaderTitleComponent';
import {ColumnsType} from 'antd/es/table';
import "./UnfinishedRoundJobs.scss";
import {
    getTotalNumberOfElementsData,
    getUnfinishedRoundsJobsData,
    getUnfinishedRoundsJobsMetadata
} from "../../../redux/selectors/configProvider/unfinished-rounds-jobs-selector";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {getUnfinishedRoundsJobs} from "../../../redux/actions/configProvider/unfinished-rounds-job-actions";
import Search from "antd/es/input/Search";
import {CloseCircleOutlined} from "@ant-design/icons";

const {Option} = Select;

interface IUnfinishedRoundJobsPagingInfo {
    pageNumber: number;
    pageSize: number;
    sortBy: string;
    order: string;
}

interface IUnfinishedRoundJobsRequestBody {
    brandIds: number[] | null;
    brandName: string | null;
    operatorName: string | null;
    actionTypes: string[] | null;
    paging: IUnfinishedRoundJobsPagingInfo;
}

interface IBrokenRoundsMetadata {
    pagingInfo: IUnfinishedRoundJobsPagingInfo;
    totalNumberOfElements: number;
}

interface IProps {
    data: IJobScheduleData[];
    getUnfinishedRoundsJobs: Function;
    totalItems: number;
    loading: boolean;
    metadata: IBrokenRoundsMetadata;
}

const DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE = 100;
const PAGE_SIZE_ARRAY = ['10', '20', '50', '100', '1000'];

const initialBodyRequest: IUnfinishedRoundJobsRequestBody = {
    paging: {
        pageNumber: 1,
        pageSize: DEFAULT_AMOUNT_OF_ITEMS_PER_PAGE,
        sortBy: 'brandName',
        order: 'ASCENDING',
    },
    brandIds: null,
    brandName: null,
    operatorName: null,
    actionTypes: null
};

const ALL_ACTIONS = ['COMPLETE', 'UNBLOCK', 'CANCEL'];
const UnfinishedRoundJobs = (props: IProps) => {
    const [searchKey, setSearchKey] = useState('brandName');
    const [actionTypes, setActionTypes] = useState(ALL_ACTIONS);
    const [searchValue, setSearchValue] = useState('');
    const [paging, setPaging] = useState(initialBodyRequest.paging);
    const {t} = useTranslation();
    const {data, getUnfinishedRoundsJobs, loading, metadata} = props;
    const getData = useMemo(
        () =>
            data
                ? data.map((jobsData) => ({
                    ...jobsData,
                    key: jobsData.brandName,
                }))
                : [],
        [data],
    );

    useEffect(() => {
        getUnfinishedRoundsJobs(initialBodyRequest);
    }, [getUnfinishedRoundsJobs]);

    const columns: ColumnsType<IJobScheduleData> = [
        {
            title: t('job_brand_name'),
            dataIndex: 'brandName',
        },
        {
            title: t('job_operator_name'),
            dataIndex: 'operatorName',
            sorter: (a, b) => a.operatorName.localeCompare(b.operatorName),
        },
        {
            title: t('job_action_type'),
            dataIndex: 'actionType',
            sorter: (a, b) => a.actionType.localeCompare(b.actionType),
        },
        {
            title: t('time_period_days'),
            dataIndex: 'timePeriodDays',
        },
        {
            title: t('timeout_hours'),
            dataIndex: 'timeoutHours',
        },
        {
            title: t('job_schedule_description'),
            dataIndex: 'scheduleDescription',
        },
        {
            title: t('job_last_run'),
            dataIndex: 'lastExecutedSuccessfullyAt',
            render: (text, record) => (
                <>
                        <span>
                            {record.lastExecutedSuccessfullyAt ? dayjs(record.lastExecutedSuccessfullyAt).format('YYYY-MM-DD HH:mm:ss') : ''}
                        </span>
                    <span className="status-message">
                            {record.message}
                        </span>
                </>
            ),
        },
    ];

    const onActionTypeFilterChange = (value: string) => {
        if (value === 'all') {
            setActionTypes(ALL_ACTIONS)
        } else {
            setActionTypes([value])
        }
    };

    const onSearchKeyChange = (key: string) => {
        setSearchKey(key);
    };

    const searchOnChange = (e: any) => {
        setSearchValue(e.target.value);
    };

    const refreshReportAfterSearch = useCallback(() => {
        const body: any = {...initialBodyRequest};
        if (searchValue.length > 0) {
            if (searchKey === 'brandIds') {
                body[searchKey] = [Number(searchValue)]; // Convert to number and wrap in array
            } else {
                body[searchKey] = searchValue;
            }
        }
        body.actionTypes = actionTypes;
        body.paging = paging;
        getUnfinishedRoundsJobs(body);
    }, [actionTypes, paging, getUnfinishedRoundsJobs, searchKey, searchValue]);

    useEffect(() => {
        refreshReportAfterSearch();
    }, [actionTypes, paging, refreshReportAfterSearch]);

    const searchFieldSuffix = () => (
        <CloseCircleOutlined
            className={`unfinished-round-jobs-search__clear${searchValue.length > 0 ? '__visible' : ''}`}
            onClick={() => {
                setSearchValue('');
                getUnfinishedRoundsJobs({
                    ...initialBodyRequest,
                    ...{
                        actionTypes: actionTypes,
                        paging: paging
                    }
                });
            }}
        />
    );

    const onSearch = () => {
        refreshReportAfterSearch();
    };

    const onPageChange = (pageNumber: number) => {
        setPaging(prevPaging => ({
            ...prevPaging,
            pageNumber: pageNumber
        }));
    };

    const onShowSizeChange = (pageNumber: number, size: number) => {
        setPaging(prevPaging => ({
            ...prevPaging,
            pageNumber: pageNumber,
            pageSize: size
        }));
    };



    return (
        <ContentComponent
            header={
                <>
                    <HeaderTitleComponent
                        title={t('job_schedule')}
                        customBreadcrumbs={<div>{t('all')}</div>}
                    />
                    <Space.Compact style={{ marginBottom: '8px', display: 'flex', width: '100%' }}>
                        <Select
                            style={{ width: '160px' }}
                            className="unfinished-round-jobs-search__action-types"
                            defaultValue="all"
                            onChange={onActionTypeFilterChange}
                            popupMatchSelectWidth={true}
                        >
                            <Option value="all">{t('all')}</Option>
                            <Option value="UNBLOCK">{t('unblock')}</Option>
                            <Option value="CANCEL">{t('cancel')}</Option>
                            <Option value="COMPLETE">{t('complete')}</Option>
                        </Select>
                        <Space.Compact className="unfinished-round-jobs-search__wrapper">
                            <Select
                                style={{ width: '160px' }}
                                defaultValue={t('brandName')}
                                className="unfinished-round-jobs-search__select"
                                onChange={onSearchKeyChange}
                                popupMatchSelectWidth={true}
                            >
                                <Option value="brandIds">{t('brandId')}</Option>
                                <Option value="brandName">{t('brandName')}</Option>
                                <Option value="operatorName">{t('operatorName')}</Option>
                            </Select>
                            <Search
                                value={searchValue}
                                onChange={searchOnChange}
                                suffix={searchFieldSuffix()}
                                className="unfinished-round-jobs__search-field"
                                placeholder={
                                    searchKey === 'sessionId'
                                        ? t('input_a_number')
                                        : t('enter_search_value')
                                }
                                onSearch={onSearch}
                                enterButton
                                style={{ flex: 1 }}
                            />
                        </Space.Compact>
                        <Space.Compact className="unfinished-round-jobs-search__pagination">
                            {data ? (
                                <Pagination
                                    className="table-pagination"
                                    onChange={onPageChange}
                                    total={metadata.totalNumberOfElements}
                                    current={metadata.pagingInfo.pageNumber}
                                    pageSize={metadata.pagingInfo.pageSize}
                                    pageSizeOptions={PAGE_SIZE_ARRAY}
                                    showSizeChanger
                                    onShowSizeChange={onShowSizeChange}
                                />
                            ) : null}
                        </Space.Compact>
                    </Space.Compact>
                </>
            }
            innerContent={
                <Table
                    pagination={false}
                    dataSource={getData}
                    columns={columns}
                    loading={loading}
                    size={'middle'}
                    footer={() => <></>}
                />
            }
        />
    );
};

const mapStateToProps = (state: any) => ({
    data: getUnfinishedRoundsJobsData(state),
    totalItems: getTotalNumberOfElementsData(state),
    metadata: getUnfinishedRoundsJobsMetadata(state)
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getUnfinishedRoundsJobs: (data: any) => dispatch(getUnfinishedRoundsJobs(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(UnfinishedRoundJobs);
